
import React from 'react';

const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="inicio" 
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-slate-50 py-20 px-4"
    >
      <div className="container mx-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Foto da Dra. representando a clínica */}
            <div className="order-2 lg:order-1 flex justify-center">
              <div className="relative">
                {/* Círculo de fundo decorativo */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-rose-300 rounded-full blur-2xl opacity-30 scale-110"></div>
                
                {/* Container da foto principal - responsivo */}
                <div className="relative w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] lg:w-[400px] lg:h-[400px] rounded-full overflow-hidden shadow-2xl border-4 sm:border-8 border-white bg-white">
                  <img 
                    src="https://static.readdy.ai/image/5d1a9eb3e1ef0df3b8285fbcdadbd077/762d92875727af4072bbc8390e0b5ad5.jpeg"
                    alt="Dentista Alphaville - Dra. Flávia Cristina Rodrigues FR Odontologia clínica odontológica moderna"
                    title="Dentista Alphaville - FR Odontologia"
                    className="w-full h-full object-cover object-top scale-105"
                  />
                  
                  {/* Overlay sutil para melhor contraste */}
                  <div className="absolute inset-0 bg-gradient-to-t from-rose-900/10 via-transparent to-transparent"></div>
                </div>

                {/* Badge da clínica - responsivo */}
                <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-white shadow-xl rounded-xl sm:rounded-2xl px-3 py-2 sm:px-6 sm:py-4 border border-rose-100">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-rose-500 rounded-full animate-pulse"></div>
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Dra. Flávia Cristina</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800">FR Odontologia</p>
                    </div>
                  </div>
                </div>

                {/* Badge de experiência da clínica - responsivo */}
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-rose-600 text-white shadow-xl rounded-xl sm:rounded-2xl px-4 py-3 sm:px-6 sm:py-4">
                  <div className="text-center">
                    <p className="text-xl sm:text-2xl font-bold">10+</p>
                    <p className="text-xs font-semibold uppercase tracking-wide">Anos</p>
                  </div>
                </div>

                {/* Elementos decorativos - ocultos no mobile */}
                <div className="hidden sm:block absolute top-12 -left-8 w-20 h-20 bg-rose-200 rounded-full blur-xl opacity-60"></div>
                <div className="hidden sm:block absolute bottom-12 -right-8 w-24 h-24 bg-slate-200 rounded-full blur-xl opacity-60"></div>
              </div>
            </div>

            {/* Texto sobre a clínica */}
            <div className="order-1 lg:order-2 text-center lg:text-left px-4 lg:px-0">
              <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-rose-50 to-white backdrop-blur-sm px-4 sm:px-8 py-3 sm:py-4 rounded-full mb-6 sm:mb-8 shadow-lg border border-rose-200">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-rose-500 rounded-full animate-pulse"></div>
                <span className="text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase">Clínica Odontológica</span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-800 mb-6 sm:mb-8 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span className="text-rose-600">Dentista Alphaville</span>
              </h1>
              
              <p className="text-lg sm:text-xl lg:text-2xl text-slate-600 mb-6 sm:mb-8 leading-relaxed font-medium px-2 lg:px-0">
                Transformando sorrisos com <span className="text-rose-600 font-semibold">excelência</span>, <span className="text-rose-600 font-semibold">tecnologia</span> e <span className="text-rose-600 font-semibold">cuidado humanizado</span>
              </p>

              <p className="text-base sm:text-lg text-slate-500 mb-8 sm:mb-12 leading-relaxed max-w-xl mx-auto lg:mx-0 px-2 lg:px-0">
                A <strong>FR Odontologia</strong> é uma <strong>clínica odontológica moderna</strong> localizada em <strong>Alphaville</strong>, oferecendo tratamentos completos em odontologia. Com a Dra. Flávia Cristina Rodrigues, realizamos procedimentos de <strong>limpeza dental</strong>, <strong>clareamento dental</strong>, restaurações dentárias, <strong>ortodontia</strong> e alinhadores invisíveis. Localizada no coração de <strong>Alphaville</strong>, no Alpha Square Mall, nossa clínica oferece um ambiente aconchegante e moderno para cuidar do seu sorriso com a máxima qualidade e conforto. Como referência em odontologia em <strong>Alphaville</strong>, proporcionamos atendimento humanizado e tecnologia avançada para todos os nossos pacientes na região de Barueri.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center lg:justify-start mb-8 sm:mb-12 px-4 lg:px-0">
                <button 
                  onClick={() => scrollToSection('contato')}
                  className="bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 text-white px-6 sm:px-10 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 sm:gap-3"
                >
                  <i className="ri-calendar-line text-lg sm:text-xl"></i>
                  Agendar Consulta
                </button>
                <a 
                  href="https://api.whatsapp.com/send?phone=5511998961030&text=Olá! Gostaria de agendar uma consulta na FR Odontologia."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 sm:px-10 py-4 sm:py-5 rounded-2xl text-base sm:text-lg font-bold transition-all duration-200 transform hover:scale-105 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 sm:gap-3"
                >
                  <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                  WhatsApp
                </a>
              </div>

              {/* Credenciais da clínica - layout responsivo */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-center lg:text-left px-4 lg:px-0">
                <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200">
                  <div className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 mb-2">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-100 rounded-full flex items-center justify-center">
                      <i className="ri-award-line text-rose-600 text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Responsável</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800">CRO-SP 113716</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200">
                  <div className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 mb-2">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-100 rounded-full flex items-center justify-center">
                      <i className="ri-heart-line text-rose-600 text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Pacientes</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800">500+ Atendidos</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-slate-200">
                  <div className="flex items-center justify-center lg:justify-start gap-2 sm:gap-3 mb-2">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-100 rounded-full flex items-center justify-center">
                      <i className="ri-star-line text-rose-600 text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Experiência</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800">10+ Anos</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
